import { ChangeDetectionStrategy, Component, EventEmitter, input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import type { IconPickerSelection } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker';
import { IonToggle, ToggleCustomEvent } from '@ionic/angular/standalone';
import type { ParsedAccordionIcon } from '../accordion/accordion-icon.util';
import { IconPickerWrapperComponent } from '../accordion/icon-picker-wrapper.component';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'dojo-gojs-node-properties-panel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IonToggle,
    TextInputComponent,
    NumberInputComponent,
    SingleSelectComponent,
    IconPickerWrapperComponent,
  ],
  templateUrl: './gojs-node-properties-panel.component.html',
  styleUrl: './gojs-node-properties-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsNodePropertiesPanelComponent {
  readonly nodeData = input.required<Record<string, unknown>>();
  readonly parsedNodeIcon = input.required<ParsedAccordionIcon | null>();
  readonly showIconPicker = input.required<boolean>();

  // Form controls passed from parent
  readonly nodeTextCtrl = input.required<FormControl<string>>();
  readonly nodeShapeCtrl = input.required<FormControl<string>>();
  readonly nodeColorThemeCtrl = input.required<FormControl<string>>();
  readonly nodeFontSizeCtrl = input.required<FormControl<number>>();
  readonly nodeFontWeightCtrl = input.required<FormControl<string>>();
  readonly nodeWidthCtrl = input.required<FormControl<number | null>>();
  readonly nodeHeightCtrl = input.required<FormControl<number | null>>();

  // Select options passed from parent
  readonly shapeOptions = input.required<SelectOption[]>();
  readonly colorThemeOptions = input.required<SelectOption[]>();
  readonly fontWeightOptions = input.required<SelectOption[]>();

  @Output() readonly sendToBack = new EventEmitter<void>();
  @Output() readonly bringToFront = new EventEmitter<void>();
  @Output() readonly textInput = new EventEmitter<void>();
  @Output() readonly mathToggle = new EventEmitter<boolean>();
  @Output() readonly borderToggle = new EventEmitter<boolean>();
  @Output() readonly colorThemeChange = new EventEmitter<string>();
  @Output() readonly iconSelected = new EventEmitter<IconPickerSelection>();
  @Output() readonly iconCleared = new EventEmitter<void>();
  @Output() readonly iconPickerToggle = new EventEmitter<boolean>();
  @Output() readonly nodeShapeChange = new EventEmitter<string>();
  @Output() readonly fontSizeChange = new EventEmitter<number | null>();
  @Output() readonly fontWeightChange = new EventEmitter<string>();
  @Output() readonly sizeChange = new EventEmitter<void>();

  getSwatchBg(theme: string): string {
    return `var(--${theme}-50)`;
  }

  getSwatchText(theme: string): string {
    return `var(--${theme}-500)`;
  }

  getSwatchBorder(theme: string): string {
    return `var(--${theme}-100)`;
  }

  ionToggleChecked(event: ToggleCustomEvent): boolean {
    return event.detail.checked;
  }
}
